import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { GridApi, GridOptions } from 'ag-grid-community';
import { combineLatest, Subscription, timer } from 'rxjs';
import { filter, first, switchMap } from 'rxjs/operators';
import { KPI } from '../../../@core/api';
import {
  I18nService,
  PerimetersService,
  PostponementsService,
  ScopeService,
  SpreadsheetColumnsService,
  SpreadsheetMenuService,
  UsersService,
} from '../../../@core/utils';
import { INVEST_FILTER_KEY_PREFIX } from '../../../@core/utils/constants';
import { AddPriceToSliceDialogComponent } from '../add-price-to-slice-dialog/add-price-to-slice-dialog.component';
import { InvestmentHistoryDialogComponent } from '../investment-history-dialog/investment-history-dialog.component';
import { PostponeSliceDialogComponent } from '../postpone-slice-dialog/postpone-slice-dialog.component';
import { AgGridAngular } from 'ag-grid-angular';

import { NbCardModule } from '@nebular/theme';

@Component({
    selector: 'app-ag-grid-postponment',
    templateUrl: './ag-grid-postponment.component.html',
    styleUrls: ['./ag-grid-postponment.component.scss'],
    standalone: true,
    imports: [
    NbCardModule,
    
    AgGridAngular
],
})
export class AgGridPostponmentComponent implements OnInit, OnChanges, OnDestroy {
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private translate: TranslateService,
    private scope: ScopeService,
    private usersService: UsersService,
    private spreadsheetMenuService: SpreadsheetMenuService,
    private spreadsheetColumnsService: SpreadsheetColumnsService,
    private postponementsService: PostponementsService,
    private perimetersService: PerimetersService,
    // must not remove this dependency, critical to have even though it's not being used
    // @ts-ignore
    private i18n: I18nService,
  ) { }

  @ViewChild('investmentHistoryDialog', { static: false })
  investmentHistoryDialog: InvestmentHistoryDialogComponent;
  @ViewChild('changeBudgetDialog', { static: false })
  changeBudgetDialog: TemplateRef<AddPriceToSliceDialogComponent>;
  @ViewChild('changeDateDialog', { static: false })
  changeDateDialog: TemplateRef<PostponeSliceDialogComponent>;

  @Input() public scenario: KPI.InvestmentScenario;
  @Input() public summaryInvestmentStatus: KPI.InvestmentStatus;
  @Output() public totalBudget = new EventEmitter<any>();
  @Output() public filtersNb = new EventEmitter<number>();

  private subscriptions: Subscription[] = [];

  private filterKeyPrefix = INVEST_FILTER_KEY_PREFIX;

  public loading: boolean = false;

  public gridOptions: GridOptions<KPI.InvestmentPostponementRowsData>;
  private gridApi: GridApi;

  private usersTransitions: KPI.AllowedStatusTransition[] = [];

  ngOnChanges() {
    if (!this.gridOptions) {
      this.gridOptions = {
        loading: false,
        rowBuffer: 10, // Draw 10 rows above and below the visible rows (Performance)
        allowDragFromColumnsToolPanel: true, // Allow user to move columns from panel
        cellSelection: {
          suppressMultiRanges: true,
        },
        rowSelection: 'multiple', // Enable to select the entire row instead of a cell
        defaultColDef: {
          sortable: true,
          resizable: true,
          editable: false,
          filter: true,
          wrapHeaderText: true,
          autoHeaderHeight: true,
          enableCellChangeFlash: true, // Flash cell after updated
          menuTabs: ['filterMenuTab', 'generalMenuTab', 'columnsMenuTab'],
        },
        columnDefs: [], // Columns definition (Set it later)
        animateRows: true, // Row animations (when expanding)
        // Always show the column menus
        suppressMenuHide: true,
        groupHeaderHeight: 0, // Hide the columns groups in the header (Set it to 20 to show)
        // Grouping column definition
        autoGroupColumnDef: {
          headerName: this.translate.instant(
            'core.utils.spreadsheet_columns.column_label',
          ),
          // field: 'investment_id', // Grouping column id
          cellRendererParams: {
            suppressCount: true, // Remove rows in group count
          },
          resizable: true,
          width: 300, // defining width to avoid the column being too big
          rowDrag: false,
          lockVisible: true,
          pinned: true,
          lockPosition: true,
          menuTabs: ['filterMenuTab', 'columnsMenuTab'],
          // valueFormatter: this.spreadsheetColumnsService.formateLabelColumn,
        },
        groupRemoveSingleChildren: true, // Remove the row groups with only one row inside
        suppressAggFuncInHeader: true, // Remove the aggregation name in column header
        context: this,
        // Restrict aggregations some functions (sum in an ag-grid native one)
        onGridReady: (params) => {
          this.gridApi = params.api;
          // Define the custom aggregation function linked to this.sumBudget()
          // Load postponement data if on the postponements page
          this.spreadsheetColumnsService.getAgGridPostponementColumns().then((cols) => {
            // Load columns
            this.gridApi.setGridOption('columnDefs', cols);
            // Load data
            this.loadAgGridData();
          });
        },
        // Hook executed after first data set
        onFirstDataRendered: () => {
          // Move the row number column while it's hidden, then show it
          // This must be done here, as the row number column is provided after the
          // grouping column one, in order to have it first in the table
          this.applyURLParamsFilters();
          this.gridApi.moveColumns(['row_nb'], 0);
        this.gridApi.setColumnsVisible(['row_nb'], true);
          this.gridApi.sizeColumnsToFit();
          this.loading = false; // End the loading spinner
        },
        // Hook executed after each data update
        onRowDataUpdated: () => {
          this.gridApi.sizeColumnsToFit();
          this.loading = false; // End the loading spinner
        },
        onColumnVisible: () => {
          this.gridApi.moveColumns(['row_nb'], 0);
        this.gridApi.setColumnsVisible(['row_nb'], true);
        },
        getContextMenuItems: () => {
          return this.buildContextMenu();
        },
      };
      // Add the sidebar to pages but not on postponements
      this.gridOptions.sideBar = {
        toolPanels: [
          {
            id: 'columns',
            labelDefault: this.translate.instant(
              'pages.common.ag-grid.columns',
            ),
            labelKey: 'columns',
            iconKey: 'columns',
            toolPanel: 'agColumnsToolPanel',
            // Hide every other tools
            toolPanelParams: {
              suppressRowGroups: true,
              suppressValues: true,
              suppressPivots: true,
              suppressPivotMode: true,
            },
          },
          {
            id: 'filters',
            labelDefault: this.translate.instant(
              'pages.common.ag-grid.filters',
            ),
            labelKey: 'filters',
            iconKey: 'filter',
            toolPanel: 'agFiltersToolPanel',
          },
        ],
        position: 'right', // Can set the side bar on the left or on the right
        // Set this to open the sidebar by default on columns panel
        // defaultToolPanel: 'columns',
      };
      this.subscriptions.push(
        combineLatest([
          this.spreadsheetColumnsService.getAgGridTranslations(),
          timer(0, 2000),
        ]).pipe(
          filter(([_trans, t]) => t <= 2),
        ).subscribe(([agGridTranslations, _timer]) => {
          this.gridOptions.localeText = agGridTranslations;
        }),
      );
    } else {
      // In case of only a change of scenario, just reload the data, not the columns
      this.loadAgGridData();
    }
  }

  ngOnInit() {
    this.subscriptions.push(
      combineLatest([
        this.scope.user$.pipe(first()),
        this.usersService.membersGroups$.pipe(first()),
      ]).subscribe(
        ([
          currentUser,
          membersGroups,
        ]) => {

          /** Get Investment Statuses **/
          const currentUserGroups = membersGroups.filter((group) =>
            group.members.includes(currentUser.id),
          );
          if (currentUserGroups) {
            // We recover all the transition allowed for the user
            currentUserGroups.forEach((group) => {
              if (group.allowed_investment_status_transitions) {
                this.usersTransitions = this.usersTransitions.concat(
                  // @ts-ignore
                  group.allowed_investment_status_transitions,
                );
              }
            });
          }
        },
      ),
    );
  }

  /**
   * Build each data row from investments + slices
   */
  protected buildRowData(postponementYear: number): Promise<any> {
    /**
     * Postponement data
     */

    return new Promise((resolve) => {
      this.perimetersService.perimeters$.pipe(
        switchMap((perimeters) => {
          const postponementFilter = {
            buildings: PerimetersService.getBuildingsIds(perimeters),
            postponement_years: [postponementYear],
          }
          return this.postponementsService
          .getPostponementsByYears(postponementFilter)
        })
      ).subscribe((postponements) => {
        resolve(postponements);
      });

    });
  }

  /**
   * Build the cells context menu
   */
  protected buildContextMenu() {
    const contextMenu = []; // Initialize context menu

    const selectedRange = this.gridApi.getCellRanges()[0]; // Get selected range

    /**
     * Get data from selected rows
     */
    const rowNodes = [];
    if (
      selectedRange &&
      typeof selectedRange.startRow !== 'undefined' &&
      typeof selectedRange.startRow.rowIndex !== 'undefined' &&
      typeof selectedRange.endRow.rowIndex !== 'undefined'
    ) {
      const start = selectedRange.startRow.rowIndex;
      const end = selectedRange.endRow.rowIndex;
      for (let i = Math.min(start, end); i <= Math.max(start, end); i++) {
        this.gridApi.forEachNodeAfterFilterAndSort((rowNode, x) => {
          if (x === i && rowNode?.data) {
            rowNodes.push(rowNode);
          }
        });
      }
    }

    // True if single select - False if multiple (+ check if row is not a group)
    const isSingleSelect = rowNodes.length === 1;
    // Get investment ids from rows selected, and remove duplicate ids

    const insightPermission =
      this.spreadsheetMenuService.userHasPermission('can_access_insight');

    /**
     * Open asset sheet menu item
     */
    if (
      insightPermission &&
      isSingleSelect &&
      rowNodes[0] &&
      rowNodes[0].asset_id
    ) {
      contextMenu.push(
        {
          name: this.translate.instant(
            'pages.decision.spreadsheet.open_asset_sheet',
          ),
          action: () => {
            this.loading = true;
            this.router.navigate(['/insight/assets/', rowNodes[0].asset_id]);
          },
        },
        'separator',
      );
    }

    /**
     * Open investment sheet menu item
     */
    if (insightPermission && isSingleSelect) {
      contextMenu.push({
        name: this.translate.instant(
          'pages.decision.spreadsheet.open_investment_sheet',
        ),
        action: () => {
          this.loading = true;
          this.router.navigate(['/insight/investments/', rowNodes[0].data.id]);
        },
      });
    }
    return contextMenu;
  }

  /**
   * 1. Retrieve the data needed for the rows generation
   * 2. Call the buildRowData function
   * 3. Load the data from buildRowData function into the Grid
   */
  private loadAgGridData() {
    /**
     * Retrieving all datas used to generate rows
     */
    this.subscriptions.push(
      combineLatest([
        this.scope.user$.pipe(first()),
        this.usersService.membersGroups$.pipe(first()),
        this.route.queryParams
      ]).subscribe(
        ([
          currentUser,
          membersGroups,
          queryParams
        ]) => {
          const postponementYear= parseInt(queryParams['invest_filter__year']) || new Date().getFullYear();
          this.loading = true;

          /** Get Investment Statuses **/
          const currentUserGroups = membersGroups.filter((group) =>
            group.members.includes(currentUser.id),
          );
          if (currentUserGroups) {
            // We recover all the transition allowed for the user
            currentUserGroups.forEach((group) => {
              if (group.allowed_investment_status_transitions) {
                this.usersTransitions = this.usersTransitions.concat(
                  // @ts-ignore
                  group.allowed_investment_status_transitions,
                );
              }
            });
          }
          this.buildRowData(postponementYear).then((rowData) => {
            if (this.gridApi) {
              this.gridApi.setGridOption('rowData', rowData); // Load data rows
            }
          });
        },
      ),
    );
  }

  /**
   * Used to retrieve filters from URL and apply it on the grid's columns
   */
  private applyURLParamsFilters() {
    this.subscriptions.push(
      this.route.queryParams.subscribe((params) => {
        // Retrieve the filter keys from URL parameters
        const filterKeys = Object.keys(params).filter((key) =>
          key.startsWith(this.filterKeyPrefix),
        );
        filterKeys.forEach((filterKey) => {
          // Get the column name from the URL param key
          const column = filterKey.substring(this.filterKeyPrefix.length);

          // Split the values from URL param
          let values = [];
          if (params[filterKey].includes('","')) {
            values = params[filterKey].split('","');
          } else {
            values = params[filterKey].split(',');
          }

          // Remove the (") from the value if any exists at the beginning or the end
          values = values.map((value) => {
            if (value.indexOf('"') === 0) {
              value = value.substring(1);
              if (value.indexOf('"') === value.length - 1) {
                return value.substring(0, value.length - 1);
              }
              return value;
            } else if (value.indexOf('"') === value.length - 1) {
              return value.substring(0, value.length - 2);
            }
            return value;
          });
          // Get the filter instance of the column
          this.gridApi.getColumnFilterInstance(column).then((filterInstance) => {
            if (filterInstance) {
              // Apply the set of values as a filter to the column
              filterInstance.setModel({
                filterType: 'set',
                values: values,
              });
            }
          });
        });
      }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
  }
}
